/**
 * Auth store (Zustand).
 *
 * Persists access + refresh tokens in localStorage. Refresh tokens being
 * accessible to JS is a tradeoff: it enables seamless refresh without a
 * service worker / cookie infra, at the cost of XSS exposure. Mitigated by:
 *   - Strict CSP (set by API responses we proxy)
 *   - Short-lived access token (15 min)
 *   - Refresh tokens rotate on every refresh
 *
 * For a more sensitive deployment, switch to httpOnly cookies + CSRF in
 * a future lot. The store interface here doesn't change.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Role } from '@weremote/shared';

export interface AuthUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: Role;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  orgSlug: string | null;

  setSession: (params: {
    accessToken: string;
    refreshToken: string;
    user: AuthUser;
    orgSlug: string;
  }) => void;

  updateTokens: (accessToken: string, refreshToken: string) => void;
  updateUser: (user: AuthUser) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      orgSlug: null,

      setSession: ({ accessToken, refreshToken, user, orgSlug }) =>
        set({ accessToken, refreshToken, user, orgSlug }),

      updateTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      updateUser: (user) => set({ user }),

      clear: () =>
        set({ accessToken: null, refreshToken: null, user: null, orgSlug: null }),
    }),
    {
      name: 'weremote-auth',
      // Don't persist accessToken — it's short-lived and we can refresh on boot.
      // Refresh token IS persisted so the user stays logged in across reloads.
      partialize: (state) => ({
        refreshToken: state.refreshToken,
        user: state.user,
        orgSlug: state.orgSlug,
      }),
    },
  ),
);
